import { useNavigate } from "react-router-dom";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Copy,
  ExternalLink,
  Eye,
  Link2,
  Pencil,
} from "lucide-react";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  getClientName,
  getAverageMonthlyValue,
  formatAverageMonthlyValue,
  formatProposalDate,
} from "../../utils";
import {
  useProposalList,
  type ProposalSortField,
} from "../../context/use-proposal-list";
import { copyToClipboard } from "@/utils/copy-to-clipboard";
import { getProposalUrl } from "@/features/vision-360/utils/proposal-url";
import { DEFAULT_ROUTES } from "@/constants/routes";

function SortHeader({
  label,
  field,
}: {
  label: string;
  field: ProposalSortField;
}) {
  const { sort, toggleSort } = useProposalList();
  const isActive = sort.field === field;
  const Icon = !isActive
    ? ArrowUpDown
    : sort.dir === "asc"
      ? ArrowUp
      : ArrowDown;
  return (
    <button
      className="flex items-center gap-1 text-sm hover:text-foreground transition-colors"
      onClick={() => toggleSort(field)}
    >
      {label}
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

export const ProposalListTable = () => {
  const { proposals, total, isLoading } = useProposalList();
  const navigate = useNavigate();

  return (
    <div className="mt-3 bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="max-h-[62vh] overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="p-4 text-base">Cliente</TableHead>
              <TableHead className="p-4 text-base">Deal ID</TableHead>
              <TableHead className="p-4 text-base">Executivo</TableHead>
              <TableHead className="p-4 text-base">
                <SortHeader
                  label="Valor médio/mês"
                  field="averageMonthlyValue"
                />
              </TableHead>
              <TableHead className="p-4 text-base">
                <SortHeader label="Data" field="createdAt" />
              </TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <>
                {Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6} className="p-4">
                      <Skeleton className="w-full h-[44px] rounded-full" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : total === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <p className="font-bold mt-6 mb-6 text-center text-lg">
                    Nenhuma proposta encontrada.
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              proposals.map((proposal) => {
                const goToDetail = () =>
                  navigate(
                    DEFAULT_ROUTES.VISION_PROPOSAL_DETAIL.replace(
                      ":uuid",
                      proposal.uuid,
                    ),
                    { state: { proposal } },
                  );
                const dealId = proposal.deal_context?.deal_id;
                return (
                  <TableRow
                    key={proposal.proposalId}
                    onClick={goToDetail}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                  >
                    <TableCell className="p-4">
                      <span className="font-medium text-sm">
                        {getClientName(proposal)}
                      </span>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {[
                          proposal.hard_constraints?.city?.split(" -")[0],
                          proposal.hard_constraints?.min_capacity != null &&
                          proposal.hard_constraints?.max_capacity != null
                            ? `${proposal.hard_constraints.min_capacity}–${proposal.hard_constraints.max_capacity} posições`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </TableCell>
                    <TableCell className="p-4 font-mono text-sm text-muted-foreground">
                      {dealId ? (
                        <span className="inline-flex items-center gap-1.5">
                          {dealId}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(dealId);
                            }}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            aria-label="Copiar Deal ID"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="p-4 text-sm">
                      {proposal.created_by?.full_name ?? "—"}
                    </TableCell>
                    <TableCell className="p-4 text-sm">
                      {formatAverageMonthlyValue(
                        getAverageMonthlyValue(proposal),
                      )}
                    </TableCell>
                    <TableCell className="p-4 text-sm text-muted-foreground">
                      {formatProposalDate(
                        proposal.updated_at ?? proposal.createdAt,
                      )}
                    </TableCell>
                    <TableCell
                      className="p-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Menubar className="border-none shadow-none relative">
                        <MenubarMenu>
                          <MenubarTrigger className="cursor-pointer px-1.5">
                            <EllipsisVerticalIcon width={20} />
                          </MenubarTrigger>
                          <MenubarContent align="end">
                            <MenubarItem
                              onClick={() =>
                                navigate(
                                  DEFAULT_ROUTES.VISION_PROPOSAL_DETAIL.replace(
                                    ":uuid",
                                    proposal.uuid,
                                  ),
                                  {
                                    state: { proposal },
                                  },
                                )
                              }
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              Visualizar proposta
                            </MenubarItem>
                            <MenubarSeparator />
                            <MenubarItem
                              onClick={() =>
                                copyToClipboard(getProposalUrl(proposal.uuid))
                              }
                            >
                              <Link2 className="mr-2 h-4 w-4" />
                              Copiar link
                            </MenubarItem>
                            <MenubarSeparator />
                            <MenubarItem
                              onClick={() =>
                                window.open(
                                  getProposalUrl(proposal.uuid),
                                  "_blank",
                                )
                              }
                            >
                              <ExternalLink className="mr-2 h-4 w-4" />
                              Abrir proposta enviada
                            </MenubarItem>
                            <MenubarSeparator />
                            <MenubarItem
                              onClick={() =>
                                window.open(
                                  `${DEFAULT_ROUTES.VISION_360_AI_MODE}?edit=${proposal.uuid}`,
                                  "_blank",
                                )
                              }
                            >
                              <Pencil className="mr-2 h-4 w-4" />
                              Editar proposta
                            </MenubarItem>
                          </MenubarContent>
                        </MenubarMenu>
                      </Menubar>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
